import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Tooltip from 'components/Tooltip'

import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'

import { color } from 'styles/colors'

export type StatusButtonProps = {
  text: string
  quantity: number | string | undefined
  icon?: React.ReactNode
  accentColor: string
  hasStripedAccentColor?: boolean
  stripSize?: string[]
  apptentiveEventName: string
  gaEventName?: string
  filter: string
  tooltip: string
  className?: string
}

const StatusButton = ({ text, quantity = 0, icon, accentColor, hasStripedAccentColor = false, stripSize = ['10px', '20px'], apptentiveEventName, gaEventName, filter, tooltip, className }: StatusButtonProps) => {
  const navigate = useNavigate()

  const onClick = () => {
    Apptentive.sendEvent(apptentiveEventName)

    GA4.sendEvent({
      category: 'Dashboard Page',
      action: 'Click Status Button',
      label: gaEventName || text
    })

    navigate(`/customers?filters=${filter}`)
  }

  return (
    <Tooltip title={tooltip} placement="top">
      <S.Button {...{ className, onClick }}>
        <S.AccentColor isStriped={hasStripedAccentColor} color={accentColor} stripSize={stripSize} />

        <S.Contents>
          {icon && <S.Icon>{icon}</S.Icon>}
          <S.Text>{text}</S.Text>
          <S.Quantity>{quantity}</S.Quantity>
        </S.Contents>
      </S.Button>
    </Tooltip>
  )
}

export default StatusButton

const S = {
  Button: styled.button`
    display: grid;
    grid-template-columns: 15px 1fr;
    padding: 0;
    justify-content: space-between;
    text-transform: capitalize;
    font-size: 15px;
    border: 1px solid ${color('border')};
    border-radius: 6px;
    color: ${color('systemStatusButtonText')};

    &:hover {
      border: 1px solid ${color('systemStatusButtonBorderHover')};
      cursor: pointer;
      box-shadow: rgba(0,0,0,0.4) 0 2px 7px 0;
    }

    &:active {
      box-shadow: rgba(0,0,0,0.4) inset 0 0 2px;
    }
  `,
  AccentColor: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isStriped', 'color', 'stripSize'].includes(prop)
  })<{ isStriped: boolean; color: string, stripSize: string[]}>`
    height: 50px;
    width: 18px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    background: ${({ isStriped, color, stripSize }) =>
    isStriped ? `repeating-linear-gradient(
        45deg,
        ${color},
        ${color} ${stripSize[0]},
        transparent ${stripSize[0]},
        transparent ${stripSize[1]}
      )` : color
};
  `,
  Contents: styled.div`
    display: flex;
    align-items: center;
    height: 45px;
    margin-left: 15px;
  `,
  Icon: styled.div`
    margin-top: 4px;
  `,
  Text: styled.div`
    margin-left: 10px;
    flex: 2 1 0;
    font-size: 12pt;
    font-family: inter.regular;
    color: ${color('statusText')};
    text-align: left;
  `,
  Quantity: styled.div`
    margin: auto;
    flex: 1 1 auto;
    font-weight: bold;
  `
}
